import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ContactGroup, Person } from '../../../../../types';
import {
  UserKeys,
  getContactGroups,
  getContactGroupContacts,
  getContacts,
  exportContacts,
  searchContacts,
} from '../../../../api/users-api';
import SharedErrorBoundary from '../../../../shared/shared-error-boundary';
import { handleErrors } from '../../../../utils/errors';
import PersonalContactsLabels from './personal-contacts-labels';
import PersonalContactsGrid from './personal-contacts-grid';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../utils/roles/client-roles';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import useDebounce from '../../../../utils/useDebounce';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

export default function PersonalContactsPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();

  const { createEvent } = useAnalytics();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedContactGroup, setSelectedContactGroup] =
    useState<ContactGroup | null>(null);
  const [selectedContact, setSelectedContact] = useState<Person | null>(null);
  const [selectedLabel, setSelectedLabel] =
    useState<string>('contactGroups/all');

  const [searchText, setSearchText] = useState('');
  const debouncedInput = useDebounce(searchText, 200);

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 100,
    page: 0,
  });

  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const {
    data: searchedContacts,
    isLoading: isSearchedContactsLoading,
    isFetching: isSearchedContactsFetching,
  } = useQuery({
    queryKey: [UserKeys.ContactSearch, userEmail, debouncedInput],
    queryFn: () => searchContacts(userEmail, debouncedInput),
    enabled: debouncedInput.length > 0,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contacts'),
    },
  });

  const {
    data: contactGroups,
    isLoading: labelsLoading,
    isFetching: labelsFetching,
  } = useQuery({
    queryKey: [UserKeys.Labels, userEmail],
    queryFn: () => getContactGroups(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contact groups'),
    },
  });

  const {
    data: groupMembers,
    isLoading: groupMembersLoading,
    isFetching: groupMembersFetching,
  } = useQuery({
    queryKey: [UserKeys.ContactGroup, userEmail, selectedLabel],
    queryFn: () => getContactGroupContacts(userEmail, selectedLabel),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group members'),
    },
  });

  function getPageOfContacts() {
    const startIndex = paginationModel.page * paginationModel.pageSize;
    if (groupMembers!.length > 100) {
      const memberSubset = groupMembers!.slice(
        startIndex,
        paginationModel.pageSize * (paginationModel.page + 1)
      );
      return memberSubset;
    } else {
      return groupMembers!;
    }
  }

  const isEnabled =
    groupMembers !== undefined && groupMembers.length > 0 && userEmail !== '';

  const {
    data,
    isLoading: contactsLoading,
    isFetching: contactsFetching,
  } = useQuery({
    queryKey: [
      UserKeys.Contacts,
      userEmail,
      selectedLabel,
      groupMembers,
      paginationModel.page,
      paginationModel.pageSize,
    ],
    queryFn: () => getContacts(userEmail, getPageOfContacts()),
    enabled: isEnabled,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('contacts'),
    },
  });

  const { mutate, isPending: isExporting } = useMutation({
    mutationFn: () => exportContacts(userEmail, 'User', changeLogMessage),
    onSuccess: (data) => {
      window.open(
        `https://docs.google.com/a/${
          userEmail.split('@')[1]
        }/spreadsheet/ccc?key=${data}#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Exporting('contacts'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .export_personal_contacts
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleMutate() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.ExportContacts.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <Stack
      flexDirection="row"
      gap={1}
      sx={{
        height: '100%',
        width: '100%',
        minHeight: 0,
      }}
    >
      <SharedErrorBoundary section="User Management - Personal Contacts: Labels">
        <PersonalContactsLabels
          userEmail={userEmail}
          userView={false}
          userRoles={userRoles}
          changeLogRoles={changeLogRoles}
          contactGroups={contactGroups}
          setSelectedRows={setSelectedRows}
          setSelectedContact={setSelectedContact}
          selectedContactGroup={selectedContactGroup}
          setSelectedContactGroup={setSelectedContactGroup}
          isLoadingLabels={labelsLoading}
          selectedLabel={selectedLabel}
          setSelectedLabel={setSelectedLabel}
        />
      </SharedErrorBoundary>

      <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0 }}>
        <PersonalContactsGrid
          userView={false}
          userRoles={userRoles}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          isLoading={
            contactsLoading ||
            contactsFetching ||
            labelsFetching ||
            labelsLoading ||
            groupMembersLoading ||
            groupMembersFetching ||
            isSearchedContactsLoading ||
            isSearchedContactsFetching
          }
          isExporting={isExporting}
          personData={debouncedInput.length > 0 ? searchedContacts : data}
          contactGroups={contactGroups}
          groupMembers={groupMembers}
          handleMutate={handleMutate}
          selectedLabel={selectedLabel}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          selectedContact={selectedContact}
          setSelectedContact={setSelectedContact}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          searchText={searchText}
          setSearchText={setSearchText}
        />

        {openChangeLogDialog ? (
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isExporting}
          />
        ) : null}
      </Box>
    </Stack>
  );
}
